<template>
  <div class="admin-property-duplicates">
    <div class="page-header">
      <h1>Doublons code lot + unit</h1>
      <p class="page-description">
        Liste des lots partageant le même <code>property_code</code> et le même
        <code>unit_code</code>. Cette combinaison doit être unique dans Altaix —
        si deux lots la partagent, l'un d'eux est un doublon à fusionner.
      </p>
    </div>

    <!-- Chargement -->
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>Chargement des doublons…</p>
    </div>

    <!-- Erreur -->
    <div v-else-if="error" class="error-state">
      <p>{{ error }}</p>
      <button class="btn btn-secondary" @click="loadDuplicates">Réessayer</button>
    </div>

    <!-- Aucun doublon -->
    <div v-else-if="groups.length === 0" class="empty-state">
      <p>✅ Aucun doublon détecté.</p>
    </div>

    <!-- Liste des groupes -->
    <div v-else class="groups-container">
      <div class="summary-bar">
        <span>{{ groups.length }} groupe(s) en doublon · {{ totalDuplicateProperties }} lots concernés</span>
        <button class="btn btn-secondary btn-sm" @click="loadDuplicates">↺ Actualiser</button>
      </div>

      <div v-for="group in groups" :key="group.propertyCode + '|||' + group.unitCode" class="duplicate-group">
        <div class="group-header">
          <div class="group-key">
            <strong>Code lot</strong> {{ group.propertyCode }}
            &nbsp;·&nbsp;
            <strong>Unit</strong> {{ group.unitCode }}
          </div>
          <div class="group-meta">{{ group.properties.length }} lots en doublon</div>
          <button
            class="btn btn-warning btn-sm"
            @click="openMergeWizard(group)"
          >
            🔀 Fusionner
          </button>
        </div>

        <table class="duplicate-table">
          <thead>
            <tr>
              <th>UUID</th>
              <th>NSIL</th>
              <th>Code immeuble</th>
              <th>AltaixID</th>
              <th>Visites</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="prop in group.properties" :key="prop.id">
              <td class="uuid-cell" :title="prop.id">{{ prop.id.slice(0, 8) }}…</td>
              <td>{{ prop.nsil || '—' }}</td>
              <td>{{ prop.buildingCode || '—' }}</td>
              <td>{{ prop.altaixId || '—' }}</td>
              <td>{{ prop.nbVisits }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- ─── WIZARD DE FUSION ──────────────────────────────────────────────── -->
    <teleport to="body">
      <div v-if="wizard.open" class="wizard-overlay" @click.self="closeWizard">
        <div class="wizard-modal">

          <!-- ÉTAPE 1 : Sélection du lot à conserver -->
          <template v-if="wizard.step === 1">
            <h2 class="wizard-title">🔀 Fusion — Choisir le lot à conserver</h2>
            <p class="wizard-hint">
              Sélectionnez le lot qui sera <strong>conservé</strong>.
              Toutes les visites des autres lots y seront transférées avant leur suppression.
            </p>

            <div class="wizard-lot-list">
              <label
                v-for="prop in wizard.group.properties"
                :key="prop.id"
                class="wizard-lot-row"
                :class="{ selected: wizard.keepId === prop.id }"
              >
                <input
                  type="radio"
                  :value="prop.id"
                  v-model="wizard.keepId"
                />
                <div class="lot-info">
                  <div class="lot-nsil">{{ prop.nsil || '—' }}</div>
                  <div class="lot-meta">
                    AltaixID : {{ prop.altaixId || '—' }} ·
                    UnitCode : {{ prop.unitCode || '—' }} ·
                    {{ prop.nbVisits }} visite(s)
                  </div>
                  <div class="lot-uuid">{{ prop.id }}</div>
                </div>
              </label>
            </div>

            <div class="wizard-footer">
              <button class="btn btn-ghost" @click="closeWizard">Annuler</button>
              <button
                class="btn btn-primary"
                :disabled="!wizard.keepId"
                @click="goToPreview"
              >
                Aperçu →
              </button>
            </div>
          </template>

          <!-- ÉTAPE 2 : Prévisualisation -->
          <template v-if="wizard.step === 2">
            <h2 class="wizard-title">🔀 Fusion — Aperçu des modifications</h2>

            <div v-if="wizard.previewLoading" class="wizard-loading">
              <div class="spinner"></div>
              <p>Chargement de l'aperçu…</p>
            </div>

            <div v-else-if="wizard.previewError" class="wizard-error">
              {{ wizard.previewError }}
              <button class="btn btn-ghost" @click="goToPreview">Réessayer</button>
            </div>

            <template v-else-if="wizard.preview">
              <!-- Lot conservé -->
              <section class="preview-section keep-section">
                <h3>✅ Lot conservé</h3>
                <div class="preview-lot">
                  <strong>{{ wizard.preview.keepProperty.nsil || '—' }}</strong>
                  <span class="lot-uuid">{{ wizard.preview.keepProperty.id }}</span>
                  <span>AltaixID : {{ wizard.preview.keepProperty.altaixId || '—' }}</span>
                  <span>{{ wizard.preview.keepProperty.nbVisits }} visite(s) existante(s)</span>
                  <span>→ aura <strong>{{ wizard.preview.keepProperty.nbVisits + wizard.preview.totalVisitsToMove }}</strong> visite(s) après fusion</span>
                </div>
              </section>

              <!-- Lots à supprimer -->
              <section class="preview-section delete-section">
                <h3>🗑 Lots à supprimer ({{ wizard.preview.deleteProperties.length }})</h3>
                <div
                  v-for="dp in wizard.preview.deleteProperties"
                  :key="dp.id"
                  class="preview-lot"
                >
                  <div class="preview-lot-header">
                    <strong>{{ dp.nsil || '—' }}</strong>
                    <span class="lot-uuid">{{ dp.id }}</span>
                    <span>AltaixID : {{ dp.altaixId || '—' }}</span>
                    <span class="badge badge-warning">{{ dp.nbVisits }} visite(s) à transférer · {{ dp.nbControls }} contrôle(s)</span>
                  </div>

                  <details v-if="dp.visits.length > 0" class="visit-list-details">
                    <summary>Voir les {{ dp.visits.length }} visite(s)</summary>
                    <table class="mini-table">
                      <thead>
                        <tr><th>Date</th><th>Technicien</th><th>Contrôles</th></tr>
                      </thead>
                      <tbody>
                        <tr v-for="v in dp.visits" :key="v.id">
                          <td>{{ formatDate(v.createdAt) }}</td>
                          <td>{{ v.technicianName }}</td>
                          <td>{{ v.nbControls }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </details>
                </div>
              </section>

              <div class="preview-totals">
                <strong>Total à transférer :</strong>
                {{ wizard.preview.totalVisitsToMove }} visite(s) ·
                {{ wizard.preview.totalControlsToMove }} contrôle(s)
              </div>
            </template>

            <div class="wizard-footer">
              <button class="btn btn-ghost" @click="wizard.step = 1">← Retour</button>
              <button class="btn btn-ghost" @click="closeWizard">Annuler</button>
              <button
                class="btn btn-danger"
                :disabled="wizard.previewLoading || !!wizard.previewError"
                @click="wizard.step = 3"
              >
                Continuer →
              </button>
            </div>
          </template>

          <!-- ÉTAPE 3 : Confirmation finale -->
          <template v-if="wizard.step === 3">
            <h2 class="wizard-title">⚠️ Confirmation de la fusion</h2>

            <div class="confirmation-warning">
              <p>
                Cette opération est <strong>irréversible</strong>. Les lots supprimés seront
                marqués comme supprimés et disparaîtront de la PWA lors de la prochaine
                synchronisation.
              </p>
              <p>
                Tapez <code>FUSIONNER</code> pour confirmer :
              </p>
              <input
                v-model="wizard.confirmationText"
                type="text"
                class="confirmation-input"
                placeholder="FUSIONNER"
                @keydown.enter="executeIfConfirmed"
              />
            </div>

            <div v-if="wizard.executeError" class="wizard-error">
              {{ wizard.executeError }}
            </div>

            <div class="wizard-footer">
              <button class="btn btn-ghost" @click="wizard.step = 2">← Retour</button>
              <button class="btn btn-ghost" @click="closeWizard">Annuler</button>
              <button
                class="btn btn-danger"
                :disabled="wizard.confirmationText !== 'FUSIONNER' || wizard.executing"
                @click="executeMerge"
              >
                <span v-if="wizard.executing">⏳ Fusion en cours…</span>
                <span v-else>✅ Confirmer la fusion</span>
              </button>
            </div>
          </template>

          <!-- ÉTAPE 4 : Succès -->
          <template v-if="wizard.step === 4">
            <h2 class="wizard-title">✅ Fusion terminée</h2>
            <div class="success-summary">
              <p>
                <strong>{{ wizard.result.visitsTransferred }}</strong> visite(s) transférée(s)
              </p>
              <p>
                <strong>{{ wizard.result.propertiesDeleted }}</strong> lot(s) supprimé(s)
              </p>
              <p class="success-note">
                La PWA sera mise à jour lors de la prochaine synchronisation.
              </p>
            </div>
            <div class="wizard-footer">
              <button class="btn btn-primary" @click="onMergeSuccess">Fermer et actualiser</button>
            </div>
          </template>

        </div>
      </div>
    </teleport>
  </div>
</template>

<script>
import apiService from '@/services/api'

export default {
  name: 'AdminPropertyDuplicates',

  data() {
    return {
      loading: false,
      error: null,
      groups: [],
      totalDuplicateProperties: 0,

      // État du wizard
      wizard: {
        open: false,
        step: 1,           // 1=sélection | 2=preview | 3=confirmation | 4=succès
        group: null,       // groupe courant
        keepId: null,      // UUID du lot à conserver

        previewLoading: false,
        previewError: null,
        preview: null,

        confirmationText: '',
        executing: false,
        executeError: null,
        result: null,
      },
    }
  },

  mounted() {
    this.loadDuplicates()
  },

  methods: {
    // ── Chargement de la liste ──────────────────────────────────────────────

    async loadDuplicates() {
      this.loading = true
      this.error = null
      try {
        const response = await apiService.getPropertyDuplicates()
        this.groups = response.groups || []
        this.totalDuplicateProperties = response.totalDuplicateProperties || 0
      } catch (err) {
        this.error = `Erreur : ${err.message}`
      } finally {
        this.loading = false
      }
    },

    // ── Wizard ──────────────────────────────────────────────────────────────

    openMergeWizard(group) {
      this.wizard = {
        open: true,
        step: 1,
        group,
        keepId: null,
        previewLoading: false,
        previewError: null,
        preview: null,
        confirmationText: '',
        executing: false,
        executeError: null,
        result: null,
      }
    },

    closeWizard() {
      this.wizard.open = false
    },

    async goToPreview() {
      if (!this.wizard.keepId) return

      this.wizard.step = 2
      this.wizard.previewLoading = true
      this.wizard.previewError = null
      this.wizard.preview = null

      const deleteIds = this.wizard.group.properties
        .filter(p => p.id !== this.wizard.keepId)
        .map(p => p.id)

      try {
        const response = await apiService.previewPropertyMerge(this.wizard.keepId, deleteIds)
        if (response.success) {
          this.wizard.preview = response
        } else {
          this.wizard.previewError = response.message || 'Erreur lors de la prévisualisation'
        }
      } catch (err) {
        this.wizard.previewError = `Erreur : ${err.message}`
      } finally {
        this.wizard.previewLoading = false
      }
    },

    executeIfConfirmed() {
      if (this.wizard.confirmationText === 'FUSIONNER') {
        this.executeMerge()
      }
    },

    async executeMerge() {
      if (this.wizard.confirmationText !== 'FUSIONNER') return

      this.wizard.executing = true
      this.wizard.executeError = null

      const deleteIds = this.wizard.group.properties
        .filter(p => p.id !== this.wizard.keepId)
        .map(p => p.id)

      try {
        const response = await apiService.executePropertyMerge(this.wizard.keepId, deleteIds)
        if (response.success) {
          this.wizard.result = response
          this.wizard.step = 4
        } else {
          this.wizard.executeError = response.message || 'Erreur lors de la fusion'
        }
      } catch (err) {
        this.wizard.executeError = `Erreur : ${err.message}`
      } finally {
        this.wizard.executing = false
      }
    },

    onMergeSuccess() {
      this.closeWizard()
      this.loadDuplicates()
    },

    // ── Utilitaires ─────────────────────────────────────────────────────────

    formatDate(dateStr) {
      if (!dateStr) return '—'
      try {
        return new Date(dateStr).toLocaleDateString('fr-FR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        })
      } catch {
        return dateStr
      }
    },
  },
}
</script>

<style scoped>
.admin-property-duplicates {
  padding: 1.5rem;
  max-width: 1200px;
  margin: 0 auto;
}

.page-header h1 {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.25rem;
}

.page-description {
  color: #6b7280;
  margin-bottom: 1.5rem;
}

/* ── États chargement / vide ── */
.loading-state,
.empty-state,
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 3rem;
  color: #6b7280;
}

.spinner {
  width: 2rem;
  height: 2rem;
  border: 3px solid #e5e7eb;
  border-top-color: #6366f1;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin { to { transform: rotate(360deg); } }

/* ── Groupes ── */
.summary-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background: #f3f4f6;
  border-radius: 0.5rem;
  margin-bottom: 1rem;
  font-size: 0.875rem;
  color: #374151;
}

.duplicate-group {
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  margin-bottom: 1.25rem;
  overflow: hidden;
}

.group-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1rem;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.group-key {
  flex: 1;
  font-family: monospace;
  font-size: 0.9rem;
}

.group-meta {
  font-size: 0.8rem;
  color: #6b7280;
}

.duplicate-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
}

.duplicate-table th,
.duplicate-table td {
  padding: 0.5rem 1rem;
  border-bottom: 1px solid #f3f4f6;
  text-align: left;
}

.duplicate-table th {
  background: #f9fafb;
  font-weight: 600;
  color: #374151;
}

.uuid-cell {
  font-family: monospace;
  font-size: 0.8rem;
  color: #6b7280;
}

/* ── Boutons ── */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s, opacity 0.15s;
}

.btn:disabled { opacity: 0.5; cursor: not-allowed; }

.btn-sm { padding: 0.25rem 0.75rem; font-size: 0.8rem; }

.btn-primary  { background: #6366f1; color: #fff; }
.btn-primary:not(:disabled):hover  { background: #4f46e5; }

.btn-danger   { background: #ef4444; color: #fff; }
.btn-danger:not(:disabled):hover   { background: #dc2626; }

.btn-warning  { background: #f59e0b; color: #fff; }
.btn-warning:not(:disabled):hover  { background: #d97706; }

.btn-secondary { background: #e5e7eb; color: #374151; }
.btn-secondary:hover { background: #d1d5db; }

.btn-ghost { background: transparent; color: #374151; border: 1px solid #d1d5db; }
.btn-ghost:hover { background: #f3f4f6; }

/* ── Wizard overlay ── */
.wizard-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.45);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.wizard-modal {
  background: #fff;
  border-radius: 1rem;
  width: min(760px, 95vw);
  max-height: 90vh;
  overflow-y: auto;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.wizard-title {
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0;
}

.wizard-hint {
  color: #6b7280;
  font-size: 0.875rem;
  margin: 0;
}

/* ── Wizard étape 1 ── */
.wizard-lot-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.wizard-lot-row {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}

.wizard-lot-row.selected {
  border-color: #6366f1;
  background: #eef2ff;
}

.wizard-lot-row input[type="radio"] {
  margin-top: 0.25rem;
  flex-shrink: 0;
}

.lot-nsil   { font-weight: 600; }
.lot-meta   { font-size: 0.8rem; color: #6b7280; margin-top: 0.15rem; }
.lot-uuid   { font-family: monospace; font-size: 0.75rem; color: #9ca3af; margin-top: 0.15rem; }

/* ── Wizard étape 2 ── */
.preview-section {
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 0.875rem 1rem;
}

.keep-section { border-color: #86efac; background: #f0fdf4; }
.delete-section { border-color: #fca5a5; background: #fef2f2; }

.preview-section h3 {
  font-size: 0.95rem;
  font-weight: 600;
  margin: 0 0 0.5rem;
}

.preview-lot {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
  font-size: 0.875rem;
  margin-bottom: 0.5rem;
}

.preview-lot-header {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
  width: 100%;
}

.badge {
  display: inline-block;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 500;
}

.badge-warning { background: #fef3c7; color: #92400e; }

.preview-totals {
  font-size: 0.875rem;
  padding: 0.5rem 1rem;
  background: #f3f4f6;
  border-radius: 0.375rem;
}

.visit-list-details {
  margin-top: 0.5rem;
}

.visit-list-details summary {
  cursor: pointer;
  font-size: 0.8rem;
  color: #6b7280;
  user-select: none;
}

.mini-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8rem;
  margin-top: 0.5rem;
}

.mini-table th,
.mini-table td {
  padding: 0.3rem 0.5rem;
  border-bottom: 1px solid #f3f4f6;
  text-align: left;
}

.mini-table th { color: #6b7280; font-weight: 600; }

/* ── Wizard étape 3 ── */
.confirmation-warning {
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 0.5rem;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  font-size: 0.875rem;
}

.confirmation-input {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 2px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 1rem;
  font-family: monospace;
  margin-top: 0.5rem;
  outline: none;
  transition: border-color 0.15s;
}

.confirmation-input:focus { border-color: #ef4444; }

/* ── Wizard étape 4 ── */
.success-summary {
  background: #f0fdf4;
  border: 1px solid #86efac;
  border-radius: 0.5rem;
  padding: 1rem;
  font-size: 0.9rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.success-note {
  color: #6b7280;
  font-size: 0.8rem;
  margin-top: 0.25rem;
}

/* ── États loading / erreur dans wizard ── */
.wizard-loading {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  color: #6b7280;
  font-size: 0.875rem;
}

.wizard-error {
  background: #fef2f2;
  border: 1px solid #fca5a5;
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  color: #b91c1c;
  font-size: 0.875rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

/* ── Footer wizard ── */
.wizard-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #f3f4f6;
}
</style>
